import { LitElement, html } from 'lit';
import '../fit-hashstate/fit-hashstate';
import '../fit-spinner/fit-spinner';
import { isEqual } from '../fit-wizard/utils/searchParams';

class FitAjaxFragment extends LitElement {
  render() {
    return html`<style>
      :host {
        display: block;
        position: relative;
        margin: 0;
      }

      #content {
        margin: 0 auto;
        max-width: 63.75rem;
        opacity: 1;
        transition: opacity .2s ease-in-out;
      }

      #content[loading] {
        opacity: .2;
        pointer-events: none;
      }

      #message {
        position: absolute;
        top: calc(50% - 2rem);
        left: calc(50% - 6rem);
      }

      .message-box {
        position: relative;
        padding: .5rem;
        width: 16rem;
        text-align: center;
        background: #fff;
        box-shadow: var(--font-accent-color) 0px 0px 5px 0px;
      }

      .message-box p {
        margin: 0 0 .5rem;
        font-size: 1rem;
        font-weight: 400;
        color: var(--secondary-color);
      }
    </style>

    <fit-hashstate @fit-hash-params-updated="${this._updateParams}"></fit-hashstate>

    <div id="content" ?loading="${this.loading}">
      <slot></slot>
    </div>

    <div id="message" ?hidden="${!this.loading}">
      <div class="message-box">
        <p>${this['label-loading']}</p>
        <fit-spinner></fit-spinner>
      </div>
    </div>`;
  }

  static get is() {
    return 'fit-ajax-fragment';
  }

  static get properties() {
    return {
      'label-loading': {
        type: String,
      },

      loading: {
        type: Boolean,
      },

      src: {
        type: String,
        reflect: true,
      },

      params: {
        type: Object,
      },
    };
  }

  constructor(props) {
    super(props);
    this.loading = false;
    this.params = null;
  }

  connectedCallback() {
    super.connectedCallback();
    this._paramsTimeout = setTimeout(() => {
      this._updateParams({ detail: {} });
    }, 500);
  }

  _updateParams(event) {
    this._paramsTimeout && clearTimeout(this._paramsTimeout);
    delete this._paramsTimeout;
    this.params = event.detail;
    this._shouldLoadContent();
  }

  _updateLoading(event) {
    setTimeout(() => { this.loading = Boolean(event.detail.value); });
  }

  _shouldLoadContent() {
    if (this.src == null || this.params == null) {
    } else {
      const { lastParams } = this;
      this.lastParams = this.params;
      if (isEqual(lastParams, this.params)) {
        return;
      }

      let url = this.src;
      if (this.params) {
        const urlSearchParams = new URLSearchParams(this.params);
        url += (url.match(/[?]/g) ? '&' : '?') + urlSearchParams.toString();
      }

      fetch(url)
        .then((response) => {
          response.text().then((responseData) => {
            this._handleResponse(responseData);
          });
        })
        .catch((err) => {
          console.error('Error fetching fragment. Error: ', err);
          this.loading = false;
        });
    }
  }

  _handleResponse(responseData) {
    this.innerHTML = responseData;

    setTimeout(() => {
      this._runScripts();
      this.dispatchEvent(new CustomEvent('fit-esi-loaded', { bubbles: true, composed: true }));
    });
  }

  _runScripts() {
    const scriptMimeTypes = ['text/javascript', 'text/ecmascript', 'application/javascript', 'application/ecmascript'];
    const scripts = this.querySelectorAll('script');
    scripts.forEach((script) => {
      const canRun = !script.hasAttribute('src') && (!script.hasAttribute('type') || scriptMimeTypes.includes(script.getAttribute('type')));
      canRun && (new Function(script.innerHTML))();
    });
  }
}

customElements.define(FitAjaxFragment.is, FitAjaxFragment);
